<template>
  <div class="wrestler-payments">
    <spinner v-if="loading"></spinner>
    <p v-else-if="error" class="text-danger">{{ errorMessage }}</p>
    <template v-else>
      <!-- On a parent's or coach's page, each kid's alerts are in their section; only ones tied to no kid stay up here -->
      <section v-if="topAlerts.length > 0" class="wp-section">
        <payments-alerts :alerts="topAlerts" :is-admin="isAdmin"></payments-alerts>
      </section>

      <!-- A wrestler's page: Enrollments, then Class passes -->
      <template v-if="!familyView">
        <payments-section class="wp-section" title="Enrollments" :count="activeEnrollments.length + endedEnrollments.length">
          <wrestler-enrollment-list :active="activeEnrollments" :ended="endedEnrollments"></wrestler-enrollment-list>
        </payments-section>
        <wrestler-class-passes
          class="wp-section"
          :wrestler-id="wrestlerId"
          :wrestler-name="wrestlerName"
          :admin-name="adminName"
          :is-admin="isAdmin"
          :subscriptions="subscriptions"
          :purchases="passPurchases"
          :hide-payments="forWrestler"
          :available-count="availableClassPassCount"
          :used-count="usedClassPassCount"
        ></wrestler-class-passes>
      </template>
      <!-- A parent's or coach's page: one section per kid (their enrollments,
           shared ones included and labeled, then their class passes), then
           Other for anything tied to no kid. Class passes are read-only here;
           admins add or use them on the wrestler's page. -->
      <template v-else>
        <payments-section
          v-for="kid in kidSections"
          :key="`kid-${kid.id}`"
          class="wp-section"
          :title="kid.name"
          :summary="kid.summary"
          :badge="kid.badge"
          :default-open="false"
        >
          <payments-alerts class="wp-kid-alerts" :alerts="kid.alerts" :is-admin="isAdmin"></payments-alerts>
          <wrestler-enrollment-list :active="kid.active" :ended="kid.ended" :wrestler-id="kid.id" heading="Enrollments"></wrestler-enrollment-list>
          <wrestler-class-passes
            class="wp-subsection"
            title="Class passes"
            :flat="true"
            :wrestler-id="kid.id"
            :wrestler-name="kid.name"
            :subscriptions="subscriptions"
            :purchases="kid.purchases"
            :manage-url="isAdmin && !forAppEmbed ? `/wrestlers/${kid.id}#tab-3` : null"
            @passes-left="setPassesLeft(kid.id, $event)"
          ></wrestler-class-passes>
        </payments-section>
        <payments-section v-if="otherEnrollments.active.length + otherEnrollments.ended.length > 0" class="wp-section" title="Other" :count="otherEnrollments.active.length + otherEnrollments.ended.length">
          <wrestler-enrollment-list :active="otherEnrollments.active" :ended="otherEnrollments.ended"></wrestler-enrollment-list>
        </payments-section>
      </template>

      <wrestler-charges-feed
        v-if="!forWrestler"
        class="wp-section"
        :charges-url="chargesUrl"
        :show-wrestlers="familyView"
        :default-open="familyView ? false : null"
        :is-admin="isAdmin"
        :viewer-billable="viewerBillable"
        :for-app-embed="forAppEmbed"
      ></wrestler-charges-feed>
    </template>
  </div>
</template>
<script>
import Spinner from './spinner.vue';
import PaymentsAlerts from './payments_alerts.vue';
import WrestlerEnrollmentList from './wrestler_enrollment_list.vue';
import WrestlerClassPasses from './wrestler_class_passes.vue';
import WrestlerChargesFeed from './wrestler_charges_feed.vue';
import PaymentsSection from './payments_section.vue';
import { formattingMixin } from './mixins/formatting_mix';
import { billingPlanMix } from './mixins/billing_plan_mix';
import { appEmbedMix } from './mixins/app_embed_mix';

const CASH_METHODS = ['cash', 'check'];
const UNPAID = ['pending', 'overdue'];
const PASS_SESSION_TYPES = ['dropin', 'trial'];
// What the cards and class passes read beyond a plain registration
const REGISTRATION_EXPANSIONS = 'expand[]=payment_plan&expand[]=class_passes&expand[]=add_ons';

// The wrestler Payments tab, on the web wrestler page and in the app embed:
// what the wrestler is enrolled in and how each is being paid, their class
// passes, and the charges behind it all. Given a guardianPath instead, it's a
// parent's or coach's Payments tab: the same for every kid in the family,
// whichever guardian is paying.
export default {
  name: 'wrestler-payments',
  components: {
    Spinner, PaymentsAlerts, WrestlerEnrollmentList, WrestlerClassPasses, WrestlerChargesFeed, PaymentsSection,
  },
  mixins: [formattingMixin, billingPlanMix, appEmbedMix],
  props: {
    wrestlerId: { type: Number, default: null },
    wrestlerName: { type: String, default: '' },
    // Family view (parent page): the parent and their wrestlers ({ id, full_name })
    // "parents/12" or "coaches/7"
    guardianPath: { type: String, default: null },
    familyWrestlers: { type: Array, default: () => [] },
    // { type, id } of the viewer. The other parent's subscription pages and
    // card aren't theirs to manage, so a parent only gets those for their own.
    viewerBillable: { type: Object, default: null },
    adminName: { type: String, default: '' },
    isAdmin: { type: Boolean, default: false },
    // The wrestler themself is viewing: what they're enrolled in, without
    // amounts, payment status, who pays or the charges feed. Financial details
    // are for their parents.
    forWrestler: { type: Boolean, default: false },
    availableClassPassCount: { type: Number, default: 0 },
    usedClassPassCount: { type: Number, default: 0 },
  },
  data() {
    return {
      loading: true,
      error: false,
      errorMessage: '',
      subscriptions: [],
      registrations: [],
      // kid id => passes left (reported by their Class passes section)
      passesLeft: {},
    };
  },
  computed: {
    familyView() {
      return this.guardianPath !== null;
    },
    chargesUrl() {
      return this.familyView ? this.$apiService.guardianChargesUrl(this.guardianPath) : this.$apiService.wrestlerChargesUrl(this.wrestlerId);
    },
    enrollments() {
      const subscriptions = this.subscriptions.map((sub) => this.subscriptionCard(sub));
      // Siblings registered for the same session on one plan share its
      // installments, so the family view shows them as one card.
      const groups = _.groupBy(
        this.registrations.filter((r) => !PASS_SESSION_TYPES.includes(r.paid_session.session_type)),
        (r) => (this.familyView && r.payment_plan ? `plan-${r.payment_plan.id}-${r.paid_session.id}` : `reg-${r.id}`),
      );
      const registrations = Object.values(groups).map((group) => this.registrationCard(group[0], group));
      return [...subscriptions, ...registrations];
    },
    activeEnrollments() {
      // Failing ones first so they sit under their alert
      return _.sortBy(this.enrollments.filter((e) => !e.ended), (e) => (e.failing ? 0 : 1));
    },
    endedEnrollments() {
      return _.orderBy(this.enrollments.filter((e) => e.ended), (e) => e.past.endedAt || '', 'desc');
    },
    // Parent / coach page: one section per kid. Every kid on the account,
    // plus anyone else an enrollment here covers (e.g. a plan this guardian
    // pays for a kid they aren't linked to). Enrollments covering several
    // kids show in each of their sections. All start collapsed; the summary
    // and Overdue badge say what's inside.
    kidSections() {
      const kids = new Map();
      this.familyWrestlers.forEach((w) => kids.set(w.id, { id: w.id, name: w.full_name }));
      this.enrollments.forEach((e) => (e.wrestlers || []).forEach((w) => {
        if (!kids.has(w.id)) {
          kids.set(w.id, { id: w.id, name: w.name });
        }
      }));
      this.passPurchases.forEach((r) => {
        const wrestler = this.registrationWrestler(r);
        if (wrestler && !kids.has(wrestler.id)) {
          kids.set(wrestler.id, wrestler);
        }
      });
      return [...kids.values()].map((kid) => {
        const covers = (e) => (e.wrestlers || []).some((w) => w.id === kid.id);
        const active = this.activeEnrollments.filter(covers);
        const ended = this.endedEnrollments.filter(covers);
        const passesLeft = this.passesLeft[kid.id] || 0;
        const failing = active.some((e) => e.failing) || this.alerts.some((a) => (a.wrestlerIds || []).includes(kid.id));
        const parts = [];
        if (active.length > 0) {
          parts.push(`${active.length} ${active.length === 1 ? 'enrollment' : 'enrollments'}`);
        }
        if (passesLeft > 0) {
          parts.push(`${passesLeft} ${passesLeft === 1 ? 'pass' : 'passes'} left`);
        }
        if (active.length === 0 && ended.length > 0) {
          parts.push(`${ended.length} past`);
        }
        return {
          ...kid,
          active,
          ended,
          purchases: this.passPurchases.filter((r) => _.get(this.registrationWrestler(r), 'id') === kid.id),
          alerts: this.alerts.filter((a) => (a.wrestlerIds || []).includes(kid.id)),
          summary: parts.join(' · ') || 'Nothing yet',
          badge: failing ? { text: 'Overdue', tone: 'danger' } : null,
        };
      });
    },
    topAlerts() {
      return this.familyView ? this.alerts.filter((a) => !a.wrestlerIds || a.wrestlerIds.length === 0) : this.alerts;
    },
    // Enrollments tied to no kid (e.g. a donation subscription billed to the guardian)
    otherEnrollments() {
      const none = (e) => !e.wrestlers || e.wrestlers.length === 0;
      return { active: this.activeEnrollments.filter(none), ended: this.endedEnrollments.filter(none) };
    },
    // Drop In / Trial purchases buy passes rather than a commitment, so they
    // live in the Class passes section, not Enrollments
    passPurchases() {
      return this.registrations.filter((r) => PASS_SESSION_TYPES.includes(r.paid_session.session_type));
    },
    alerts() {
      if (this.forWrestler) {
        return [];
      }
      const alerts = [];
      this.subscriptions.filter((sub) => sub.status === 'overdue').forEach((sub) => {
        alerts.push({
          ...this.cardFailureAlert(`sub-${sub.id}`, this.productNames(sub) || 'Subscription', _.get(sub, 'billing_profile.billable')),
          wrestlerIds: this.subscriptionWrestlers(sub).map((w) => w.id),
        });
      });
      // Installments belong to the plan, which siblings can share
      const planWrestlerIds = _.mapValues(
        _.groupBy(this.registrations.filter((r) => r.payment_plan), (r) => r.payment_plan.id),
        (regs) => _.uniq(regs.map((r) => _.get(this.registrationWrestler(r), 'id')).filter(Boolean)),
      );
      _.uniqBy(this.registrations, (r) => (r.payment_plan ? r.payment_plan.id : `reg-${r.id}`)).forEach((registration) => {
        const installments = _.get(registration, 'payment_plan.installments', []);
        const wrestlerIds = registration.payment_plan ? planWrestlerIds[registration.payment_plan.id] : _.compact([_.get(this.registrationWrestler(registration), 'id')]);
        // Nothing to collect on a $0 installment
        const cashDue = installments.find((i) => CASH_METHODS.includes(i.payment_method) && UNPAID.includes(i.status) && this.isDue(i.due_on) && i.amount_due > 0);
        if (cashDue) {
          const due = `${this.formatAsCurrency(cashDue.amount_due)} was due ${this.shortDate(cashDue.due_on)}.`;
          alerts.push({
            key: `cash-${cashDue.id}`,
            tone: 'warning',
            title: `${registration.paid_session.name} ${cashDue.payment_method} payment is overdue`,
            body: this.isAdmin ? `${due} Mark it paid once you've collected it.` : `${due} Pay the team by ${cashDue.payment_method}.`,
            invoice: cashDue,
            wrestlerIds,
          });
        } else if (installments.some((i) => i.payment_method === 'card' && i.status === 'overdue')) {
          alerts.push({ ...this.cardFailureAlert(`reg-${registration.id}`, registration.paid_session.name, registration.payment_plan.billed_to), wrestlerIds });
        }
      });
      return alerts;
    },
  },
  created() {
    this.load();
    this.$notificationManager.on('invoice-paid', this.invoicePaid);
    // A used or added pass changes a purchase's counts (wrestler-class-passes
    // reloads the coach-added ones itself)
    this.$notificationManager.on('class-pass-changed', this.load);
  },
  destroyed() {
    this.$notificationManager.off('invoice-paid', this.invoicePaid);
    this.$notificationManager.off('class-pass-changed', this.load);
  },
  methods: {
    load() {
      const api = this.$apiService;
      const subscriptionsUrl = this.familyView ? api.guardianBillingSubscriptionsUrl(this.guardianPath) : api.wrestlerBillingSubscriptions(this.wrestlerId);
      const registrationsUrl = this.familyView ? api.guardianRegistrationsUrl(this.guardianPath) : api.wrestlerRegistrationsUrl(this.wrestlerId);
      // A wrestler gets no payment details (the API leaves payment_plan out for them too)
      const registrationExpansions = this.forWrestler ? 'expand[]=class_passes&expand[]=add_ons' : REGISTRATION_EXPANSIONS;
      Promise.all([
        api.loadAllPages(`${subscriptionsUrl}${this.forWrestler ? '' : '?expand[]=renewals'}`, 'subscriptions'),
        api.loadAllPages(`${registrationsUrl}?${registrationExpansions}`, 'registrations'),
      ])
        .then(([subscriptions, registrations]) => {
          this.subscriptions = subscriptions;
          this.registrations = registrations;
          this.loading = false;
        })
        .catch((error) => {
          this.loading = false;
          this.error = true;
          this.errorMessage = `Error loading payments ${error.toString()}`;
        });
    },
    invoicePaid() {
      this.load();
    },
    cardFailureAlert(key, name, billable) {
      let body = 'Your card on file was declined. Check your email for the link to update your card on file.';
      if (this.isAdmin) {
        body = 'The card on file was declined. Have the family check their email for the link to update their card on file.';
      } else if (billable && !this.billedToViewer(billable)) {
        body = `The card on file for ${billable.display_name} was declined. They can check their email for the link to update it.`;
      }
      return {
        key, tone: 'danger', title: `${name} payment failed`, body, invoice: null,
      };
    },
    // The registered wrestler, as { id, name }
    registrationWrestler(registration) {
      const { profile } = registration;
      return profile && profile.type === 'WrestlerProfile' ? { id: profile.id, name: profile.display_name } : null;
    },
    billedToViewer(billable) {
      return !!(this.viewerBillable && billable && billable.type === this.viewerBillable.type && billable.id === this.viewerBillable.id);
    },
    // Subscription pages 403 for anyone but admins and whoever is billed
    canManage(billable) {
      return !this.forAppEmbed && (this.isAdmin || this.billedToViewer(billable));
    },
    // What the Next charge fact says on a paid subscription, or null to leave
    // it off. Always says something when there's an answer, so a paused or
    // fully billed contract doesn't just lose the row. Same last-period rule as
    // billingPlanMix#readableEndAt ("all cycles billed").
    nextChargeValue(sub, paused) {
      if (paused) {
        return sub.payment_collection_resumes_at ? `Paused · resumes ${this.shortDate(sub.payment_collection_resumes_at)}` : 'Paused · no resume date';
      }
      if (sub.expires_on && sub.current_period_end_at && moment(sub.expires_on).diff(moment(sub.current_period_end_at), 'days') <= 2) {
        return 'None · all payments made';
      }
      if (!sub.current_period_end_at || moment(sub.current_period_end_at).isBefore(moment())) {
        return null;
      }
      return this.shortDate(sub.current_period_end_at);
    },
    isDue(dueOn) {
      return dueOn && moment(dueOn).isSameOrBefore(moment(), 'day');
    },
    shortDate(value) {
      if (!value) {
        return '';
      }
      const date = moment(value);
      return date.year() === moment().year() ? date.format('MMM D') : date.format('MMM D, YYYY');
    },
    // --- Card view models ---------------------------------------------------
    subscriptionCard(sub) {
      const paused = sub.payment_collection_status === 'paused';
      const badge = {
        canceled: { text: 'Canceled', tone: 'neutral' },
        overdue: { text: 'Overdue', tone: 'danger' },
        paid: paused ? { text: 'Paused', tone: 'warning' } : { text: 'Active', tone: 'success' },
        partially_paid: { text: 'Partially paid', tone: 'success' },
        pending: { text: 'Pending', tone: 'neutral' },
      }[sub.status] || { text: _.startCase(sub.status || 'pending'), tone: 'neutral' };
      const coupon = this.forWrestler ? null : _.first(sub.coupons);
      const facts = this.forWrestler ? [] : [
        { label: 'Price', value: `${this.formatAsCurrency(this.subscriptionTotal(sub))}${this.subInterval(sub)}` },
      ];
      const nextCharge = !this.forWrestler && sub.status === 'paid' ? this.nextChargeValue(sub, paused) : null;
      if (nextCharge) {
        facts.push({ label: 'Next charge', value: nextCharge });
      }
      // Contract term and renewal right under the price: when it ends matters most
      const renewal = this.renewalFacts(sub);
      facts.push(...renewal.facts);
      if (coupon) {
        facts.push({ label: 'Discount', coupon: { code: coupon.code, detail: this.couponDetail(coupon) } });
      }
      facts.push({ label: 'Subscribed since', value: this.shortDate(sub.created_at) });
      const billable = _.get(sub, 'billing_profile.billable');
      if (billable && !this.forWrestler) {
        facts.push({ label: 'Paid by', value: this.parentName(false, sub), wide: true });
      }
      const classLimit = this.subscriptionClassPassLabel(sub);
      if (classLimit) {
        facts.push({ label: 'Class limit', value: `${classLimit} (see Class passes)`, wide: true });
      }
      return {
        key: `sub-${sub.id}`,
        typeLabel: 'Recurring Subscription',
        title: this.productNames(sub) || 'Subscription',
        wrestlers: this.subscriptionWrestlers(sub),
        who: null,
        badge: this.forWrestler ? this.enrolledBadge(sub.status) : (sub.status === 'paid' && !paused && renewal.badge) || badge,
        failing: !this.forWrestler && sub.status === 'overdue',
        facts,
        insurance: [],
        progress: null,
        // The subscription page needs a web session, so the embed can't open it.
        link: this.canManage(billable) ? { text: 'Manage subscription', href: `/subscriptions/${sub.billing_partner_id}` } : null,
        ended: sub.status === 'canceled',
        past: this.subscriptionPast(sub, billable),
      };
    },
    // group: the registration plus any siblings on the same plan and session
    registrationCard(registration, group = [registration]) {
      const plan = registration.payment_plan;
      const names = _.uniq(_.compact(group.map((r) => _.get(this.registrationWrestler(r), 'name'))));
      const installments = _.get(plan, 'installments', []).filter((i) => i.status !== 'void');
      const total = _.sumBy(installments, 'amount_due');
      const paid = _.sumBy(installments, (i) => i.amount_paid || 0);
      const paidCount = installments.filter((i) => i.status === 'paid').length;
      const next = installments.find((i) => UNPAID.includes(i.status));
      const cash = next && CASH_METHODS.includes(next.payment_method);

      const badge = this.registrationBadge(registration, installments, paidCount);

      const facts = [];
      if (next && !this.forWrestler) {
        facts.push({ label: installments.length > 1 ? 'Next installment' : 'Due', value: `${this.formatAsCurrency(next.amount_due)} · ${this.shortDate(next.due_on)}${cash ? ` · ${next.payment_method}` : ''}` });
      }
      if (registration.payment_option && registration.payment_option.name) {
        facts.push({ label: 'Option', value: registration.payment_option.name });
      }
      const division = _.compact([_.get(registration, 'age_division.name'), _.get(registration, 'weight_class.name')]).join(' · ');
      if (division) {
        facts.push({ label: 'Division', value: division });
      }
      if (registration.add_ons.length > 0) {
        facts.push({ label: 'Add-ons', value: registration.add_ons.join(', ') });
      }
      if (plan && plan.billed_to && !this.forWrestler) {
        const others = plan.other_wrestlers.filter((name) => !names.includes(name));
        const shared = others.length > 0 ? ` · plan also covers ${others.join(', ')}` : '';
        facts.push({ label: 'Paid by', value: `${plan.billed_to.display_name}${shared}`, wide: true });
      }

      // A season or camp is over once its session has ended and nothing is
      // still owed; archived sessions and canceled registrations are always over.
      const sessionOver = registration.paid_session.end_at && moment(registration.paid_session.end_at).isBefore(moment(), 'day');
      const ended = registration.status === 'canceled' || registration.paid_session.archived || (sessionOver && !next);
      return {
        key: `reg-${registration.id}`,
        typeLabel: registration.paid_session.session_type_label,
        title: registration.paid_session.name,
        wrestlers: _.compact(group.map((r) => this.registrationWrestler(r))),
        who: null,
        badge: this.forWrestler ? this.enrolledBadge(registration.status) : badge,
        failing: !this.forWrestler && (registration.status === 'overdue' || installments.some((i) => i.status === 'overdue')),
        progress: installments.length > 0 && !this.forWrestler ? {
          paid: this.formatAsCurrency(paid),
          total: this.formatAsCurrency(total),
          percent: total > 0 ? Math.min(100, Math.round((paid / total) * 100)) : 0,
          label: installments.length > 1 ? `${paidCount} of ${installments.length} installments` : 'Pay in full',
        } : null,
        facts,
        insurance: _.flatMap(group, (r) => r.insurance_policies.map((policy) => this.insuranceLine(policy, group.length > 1 ? _.get(this.registrationWrestler(r), 'name') : null))),
        link: (this.isAdmin && !this.forAppEmbed) ? { text: 'View registration', href: `/paid_sessions/${registration.paid_session.id}` } : null,
        ended,
        past: this.registrationPast(registration, {
          paid, total, sessionOver,
        }),
      };
    },
    insuranceLine(policy, wrestlerName = null) {
      const badge = {
        bound: { text: 'Active', tone: 'success' },
        pending_payment: { text: 'Processing', tone: 'neutral' },
        sent_to_justifi: { text: 'Processing', tone: 'neutral' },
      }[policy.status] || { text: 'Failed', tone: 'danger' };
      const parts = _.compact([wrestlerName, policy.product_friendly_name || _.startCase(policy.product_name || 'Insurance')]);
      if (policy.total && !this.forWrestler) {
        parts.push(this.formatAsCurrency(policy.total));
      }
      if (policy.policy_number) {
        parts.push(`policy ${policy.policy_number}`);
      }
      // Processing / Failed are about the payment
      return { key: policy.id, text: parts.join(' · '), badge: this.forWrestler && policy.status !== 'bound' ? null : badge };
    },
    // "50% off" / "$10.00 off" next to the code (amount_off is cents, percent_off a fraction)
    couponDetail(coupon) {
      if (coupon.discount_strategy === 'percent_off' && coupon.percent_off) {
        return `${Math.round(coupon.percent_off * 100)}% off`;
      }
      if (coupon.discount_strategy === 'amount_off' && coupon.amount_off) {
        return `${this.formatAsCurrency(coupon.amount_off)} off`;
      }
      return null;
    },
    // From the installments rather than the stored status: some checkouts
    // (auto-approved team registrations) save the plan as paid while cash is
    // still owed.
    registrationBadge(registration, installments, paidCount) {
      if (registration.status === 'canceled') {
        return { text: 'Canceled', tone: 'neutral' };
      }
      if (registration.status === 'awaiting_approval') {
        return { text: 'Awaiting approval', tone: 'warning' };
      }
      if (installments.length === 0) {
        return {
          paid: { text: 'Paid', tone: 'success' },
          overdue: { text: 'Overdue', tone: 'danger' },
        }[registration.status] || { text: _.capitalize(registration.status || 'pending'), tone: 'neutral' };
      }
      if (installments.some((i) => i.status === 'overdue')) {
        return { text: 'Overdue', tone: 'danger' };
      }
      if (paidCount === installments.length) {
        return { text: 'Paid', tone: 'success' };
      }
      if (paidCount > 0) {
        return { text: installments.length > 1 ? `${paidCount} of ${installments.length} paid` : 'Partially paid', tone: 'success' };
      }
      return { text: 'Not paid yet', tone: 'neutral' };
    },
    // What a wrestler sees instead of payment status: whether they're in it
    enrolledBadge(status) {
      return {
        canceled: { text: 'Canceled', tone: 'neutral' },
        awaiting_approval: { text: 'Awaiting approval', tone: 'warning' },
      }[status] || { text: 'Active', tone: 'success' };
    },
    // Contract (expiring) subscriptions: the term, past renewals, and where the
    // renewal stands. renew! extends the same subscription, so without this the
    // card can't tell a first term from a third. Reminder emails go out 14, 5
    // and 1 days before it ends.
    renewalFacts(sub) {
      const { renewal } = sub;
      if (!sub.expires_on) {
        return { facts: [], badge: null };
      }
      const facts = [];
      const ends = this.shortDate(sub.expires_on);
      // "Expires", like the old card and the pause emails, so families find the date
      facts.push({ label: 'Expires', value: renewal && renewal.term_label ? `${ends} (${renewal.term_label})` : ends });
      if (!renewal || sub.status === 'canceled' || this.forWrestler) {
        return { facts, badge: null };
      }
      // An overdue subscription can't renew until it's paid; its alert says so
      facts.push({
        label: 'Renewed',
        value: renewal.times_renewed > 0
          ? `${renewal.times_renewed} ${renewal.times_renewed === 1 ? 'time' : 'times'} · last ${this.shortDate(renewal.last_renewed_at)}`
          : 'Not yet (first term)',
      });
      let badge = null;
      if (sub.status !== 'paid') {
        return { facts, badge };
      }
      if (!renewal.renewable) {
        facts.push({ label: 'Renewal', value: "Can't be renewed online", wide: true });
      } else if (moment().isSameOrAfter(moment(renewal.reminders_start_at))) {
        facts.push({ label: 'Renewal', value: 'Not renewed yet · reminder emails are going out', wide: true });
        badge = { text: 'Renewal due', tone: 'warning' };
      } else {
        facts.push({ label: 'Renewal', value: `Reminder emails start ${this.shortDate(renewal.reminders_start_at)}`, wide: true });
      }
      return { facts, badge };
    },
    // The kids a subscription covers (fee and tax items aside)
    subscriptionWrestlers(sub) {
      const wrestlers = [];
      sub.billing_plan_subscription_items.forEach((item) => {
        const plan = this.planForItem(item, sub);
        if (plan && !plan.is_wiq_fee && !plan.is_tax_item) {
          item.wrestler_profiles.forEach((w) => wrestlers.push({ id: w.id, name: `${w.first_name} ${w.last_name}` }));
        }
      });
      return _.uniqBy(wrestlers, 'id');
    },
    setPassesLeft(wrestlerId, count) {
      this.$set(this.passesLeft, wrestlerId, count);
    },
    // --- Past enrollments ------------------------------------------------------
    // { badge, when, dateText, link, endedAt }: how and when it ended, for its
    // card in Past enrollments (newest first) and the collapsed preview
    // (dateText: the end date alone, when there is one)
    subscriptionPast(sub, billable) {
      const failed = !this.forWrestler && sub.cancellation_cause === 'payment_failure';
      const expired = sub.cancellation_cause === 'expired';
      let badge = { text: 'Canceled', tone: 'neutral' };
      if (failed) {
        badge = { text: 'Canceled', tone: 'danger' };
      } else if (expired) {
        badge = { text: 'Ended', tone: 'neutral' };
      }
      const date = sub.canceled_at || sub.expires_on;
      const why = { payment_failure: failed ? 'after failed payments' : null, expired: 'term ended' }[sub.cancellation_cause];
      return {
        badge,
        when: _.compact([date && this.shortDate(date), why]).join(' · '),
        dateText: date ? this.shortDate(date) : null,
        link: this.canManage(billable) ? { text: 'View subscription', href: `/subscriptions/${sub.billing_partner_id}` } : null,
        endedAt: date || sub.updated_at,
      };
    },
    registrationPast(registration, {
      paid, total, sessionOver,
    }) {
      const plan = registration.payment_plan;
      const link = (this.isAdmin && !this.forAppEmbed) ? { text: 'View registration', href: `/paid_sessions/${registration.paid_session.id}` } : null;
      if (registration.status === 'canceled') {
        const failed = _.get(plan, 'cancellation_cause') === 'payment_failure';
        const date = registration.canceled_at || _.get(plan, 'canceled_at');
        return {
          badge: { text: 'Canceled', tone: failed ? 'danger' : 'neutral' },
          when: _.compact([date && this.shortDate(date), failed ? 'after failed payments' : null]).join(' · '),
          dateText: date ? this.shortDate(date) : null,
          link,
          endedAt: date || registration.created_at,
        };
      }
      // An archived session can still have money owed on it
      const owed = total - paid;
      const unpaid = _.get(plan, 'installments', []).some((i) => UNPAID.includes(i.status));
      if (unpaid && owed > 0 && !this.forWrestler) {
        return {
          badge: { text: 'Unpaid', tone: 'danger' },
          when: _.compact([`${this.formatAsCurrency(owed)} owed`, sessionOver ? `ended ${this.shortDate(registration.paid_session.end_at)}` : 'session archived']).join(' · '),
          link,
          endedAt: sessionOver ? registration.paid_session.end_at : registration.created_at,
        };
      }
      return {
        badge: { text: 'Ended', tone: 'neutral' },
        when: sessionOver ? this.shortDate(registration.paid_session.end_at) : 'Session archived',
        dateText: sessionOver ? this.shortDate(registration.paid_session.end_at) : null,
        link,
        endedAt: sessionOver ? registration.paid_session.end_at : registration.created_at,
      };
    },
  },
};
</script>
